import dayjs, { Dayjs } from "dayjs"
import { CrewMember, UpdateScheduledEventRequest, DateRangeViewEvent, ConsultationCalendarEvent, TaskCalendarEvent } from "../scheduleApi"
import { findCrewMemberByEmail } from "../crewModel"
import { ConsultationColumn } from "./WeekViewConsultation"

export type BuildMoveEventRequestParams = {
  event: DateRangeViewEvent
  targetDate: Dayjs
  targetMinutes: number | null
  crew: CrewMember[]
}

export const buildMoveEventRequest = ({
  event,
  targetDate,
  targetMinutes,
  crew,
}: BuildMoveEventRequestParams): UpdateScheduledEventRequest | null => {
  const originalStart = dayjs(event.start)
  const originalEnd = dayjs(event.end)
  const daysDiff = targetDate.startOf('day').diff(originalStart.startOf('day'), 'day')
  const durationMinutes = originalEnd.diff(originalStart, 'minutes')

  let newStart: Dayjs
  let newEnd: Dayjs

  if (targetMinutes !== null) {
    const targetHours = Math.floor(targetMinutes / 60)
    const targetMins = targetMinutes % 60
    newStart = targetDate.startOf('day').hour(targetHours).minute(targetMins)
    newEnd = newStart.add(durationMinutes, 'minute')
  } else {
    newStart = originalStart.add(daysDiff, 'day')
    newEnd = originalEnd.add(daysDiff, 'day')
  }

  const originalStartMinutes = originalStart.hour() * 60 + originalStart.minute()
  const newStartMinutes = newStart.hour() * 60 + newStart.minute()

  if (daysDiff === 0 && originalStartMinutes === newStartMinutes) {
    return null
  }

  const crewLead = event.crewForemanEmail
    ? findCrewMemberByEmail(crew, event.crewForemanEmail)
    : null

  return {
    eventId: event.id,
    start: newStart.toISOString(),
    end: newEnd.toISOString(),
    dayStartTime: null,
    estimatedDurationInMinutes: durationMinutes,
    crewMemberIds: event.crewMemberIds || '',
    crewLeadId: crewLead?.id ?? null,
    workareaIds: event.workAreaIds || '',
    routeId: event.routeId,
    allDay: event.allDay,
    sendCrewLeadNotifications: event.sendCrewLeadNotifications,
    frozen: event.frozen
  }
}

export const TIME_COLUMN_WIDTH = 60
export const HOUR_HEIGHT = 120
export const MIN_EVENT_HEIGHT = 30
export const START_HOUR = 0
export const END_HOUR = 24
export const TOTAL_HOURS = END_HOUR - START_HOUR
export const getEventTopOffset = (event: { start: string }): number => {
  const start = dayjs(event.start)
  const hours = start.hour()
  const minutes = start.minute()
  const totalMinutes = (hours - START_HOUR) * 60 + minutes
  return (totalMinutes / 60) * HOUR_HEIGHT
}

export const getEventHeight = (event: { start: string; end: string }): number => {
  const start = dayjs(event.start)
  const end = dayjs(event.end)
  const durationMinutes = end.diff(start, 'minutes')
  const height = (durationMinutes / 60) * HOUR_HEIGHT
  return Math.max(height, MIN_EVENT_HEIGHT)
}

export type EventColumn = {
  event: DateRangeViewEvent
  column: number
  totalColumns: number
}

const MIN_VISUAL_DURATION_MS = (MIN_EVENT_HEIGHT / HOUR_HEIGHT) * 60 * 60 * 1000

const getVisualEndTime = (event: DateRangeViewEvent): number => {
  const start = dayjs(event.start).valueOf()
  const end = dayjs(event.end).valueOf()
  return Math.max(end, start + MIN_VISUAL_DURATION_MS)
}

const eventsOverlap = (a: DateRangeViewEvent, b: DateRangeViewEvent): boolean => {
  const aStart = dayjs(a.start).valueOf()
  const aEnd = getVisualEndTime(a)
  const bStart = dayjs(b.start).valueOf()
  const bEnd = getVisualEndTime(b)
  return aStart < bEnd && bStart < aEnd
}

export const calculateEventColumns = (events: DateRangeViewEvent[]): EventColumn[] => {
  if (events.length === 0) {
    return []
  }

  const sortedEvents = [...events].sort((a, b) =>
    dayjs(a.start).valueOf() - dayjs(b.start).valueOf()
  )

  const clusters: DateRangeViewEvent[][] = []

  for (const event of sortedEvents) {
    let addedToCluster = false

    for (const cluster of clusters) {
      const overlapsWithCluster = cluster.some(e => eventsOverlap(e, event))
      if (overlapsWithCluster) {
        cluster.push(event)
        addedToCluster = true
        break
      }
    }

    if (!addedToCluster) {
      clusters.push([event])
    }
  }

  const result: EventColumn[] = []

  for (const cluster of clusters) {
    const clusterSorted = [...cluster].sort((a, b) =>
      dayjs(a.start).valueOf() - dayjs(b.start).valueOf()
    )

    const columnEnds: number[] = []
    const clusterColumns: { event: DateRangeViewEvent; column: number }[] = []

    for (const event of clusterSorted) {
      const eventStart = dayjs(event.start).valueOf()
      const eventEnd = getVisualEndTime(event)

      let assignedColumn = -1
      for (let i = 0; i < columnEnds.length; i++) {
        if (columnEnds[i] <= eventStart) {
          assignedColumn = i
          columnEnds[i] = eventEnd
          break
        }
      }

      if (assignedColumn === -1) {
        assignedColumn = columnEnds.length
        columnEnds.push(eventEnd)
      }

      clusterColumns.push({ event, column: assignedColumn })
    }

    const totalColumns = columnEnds.length

    for (const col of clusterColumns) {
      result.push({
        event: col.event,
        column: col.column,
        totalColumns,
      })
    }
  }

  return result
}

export const DEFAULT_COLLAPSE_END_HOUR = 6

export const calculateCollapseEndHour = (
  events: DateRangeViewEvent[],
  consultations: ConsultationCalendarEvent[],
  tasks: TaskCalendarEvent[] = []
): number => {
  const hours = [...events, ...consultations, ...tasks].map(x => dayjs(x.start).hour())
  if (hours.length === 0) {
    return DEFAULT_COLLAPSE_END_HOUR
  }
  const earliestHour = Math.min(...hours)
  return Math.min(earliestHour, DEFAULT_COLLAPSE_END_HOUR)
}

export const snapToGrid = (minutes: number): number => {
  return Math.round(minutes / 30) * 30
}

export const snapTo15Minutes = (minutes: number): number => {
  return Math.round(minutes / 15) * 15
}

export type BuildResizeEventRequestParams = {
  event: DateRangeViewEvent
  newDurationMinutes: number
  crew: CrewMember[]
}

export const buildResizeEventRequest = ({
  event,
  newDurationMinutes,
  crew,
}: BuildResizeEventRequestParams): UpdateScheduledEventRequest | null => {
  const originalStart = dayjs(event.start)
  const originalEnd = dayjs(event.end)
  const originalDuration = originalEnd.diff(originalStart, 'minutes')

  if (newDurationMinutes === originalDuration || newDurationMinutes < 15) {
    return null
  }

  const newEnd = originalStart.add(newDurationMinutes, 'minute')

  const crewLead = event.crewForemanEmail
    ? findCrewMemberByEmail(crew, event.crewForemanEmail)
    : null

  return {
    eventId: event.id,
    start: originalStart.toISOString(),
    end: newEnd.toISOString(),
    dayStartTime: null,
    estimatedDurationInMinutes: newDurationMinutes,
    crewMemberIds: event.crewMemberIds || '',
    crewLeadId: crewLead?.id ?? null,
    workareaIds: event.workAreaIds || '',
    routeId: event.routeId,
    allDay: event.allDay,
    sendCrewLeadNotifications: event.sendCrewLeadNotifications,
    frozen: event.frozen
  }
}

export const getMinutesFromOffset = (offsetY: number): number => {
  const rawMinutes = (offsetY / HOUR_HEIGHT) * 60 + START_HOUR * 60
  return snapToGrid(rawMinutes)
}

export const getMinutesFromOffset15 = (offsetY: number): number => {
  const rawMinutes = (offsetY / HOUR_HEIGHT) * 60 + START_HOUR * 60
  return snapTo15Minutes(rawMinutes)
}

export type SelectionPreview = {
  startMinutes: number
  endMinutes: number
}

export const getMaxOverlapForDay = (
  events: DateRangeViewEvent[],
  consultations: ConsultationCalendarEvent[],
  tasks: TaskCalendarEvent[] = []
): number => {
  const { eventColumns, consultationColumns, taskColumns } = calculateCombinedColumns(events, consultations, tasks)
  const allColumns = [...eventColumns, ...consultationColumns, ...taskColumns]
  return allColumns.length > 0 ? Math.max(...allColumns.map(col => col.totalColumns)) : 1
}

export const MIN_EVENT_COLUMN_WIDTH = 120

export const calculateDayColumnWidths = (
  weekDays: Dayjs[],
  eventsByDay: Map<string, DateRangeViewEvent[]>,
  consultationsByDay: Map<string, ConsultationCalendarEvent[]>,
  containerWidth: number,
  tasksByDay: Map<string, TaskCalendarEvent[]> = new Map(),
  hasSpanningByDay: boolean[] = []
): number[] => {
  const availableWidth = containerWidth - TIME_COLUMN_WIDTH
  const baseWidth = availableWidth / 7

  const MIN_DAY_WIDTH = 120
  const isEmptyWeekend = weekDays.map((day, index) => {
    const dateKey = day.format('YYYY-MM-DD')
    const dayEvents = eventsByDay.get(dateKey) ?? []
    const dayConsultations = consultationsByDay.get(dateKey) ?? []
    const dayTasks = tasksByDay.get(dateKey) ?? []
    const hasSpanning = hasSpanningByDay[index] ?? false
    const isWeekend = day.day() === 0 || day.day() === 6
    return isWeekend && dayEvents.length === 0 && dayConsultations.length === 0 && dayTasks.length === 0 && !hasSpanning
  })

  const rawWidths = weekDays.map((day, i) => {
    if (isEmptyWeekend[i]) {
      return baseWidth / 3
    }

    const dateKey = day.format('YYYY-MM-DD')
    const dayEvents = eventsByDay.get(dateKey) ?? []
    const dayConsultations = consultationsByDay.get(dateKey) ?? []
    const dayTasks = tasksByDay.get(dateKey) ?? []
    const maxOverlap = getMaxOverlapForDay(dayEvents, dayConsultations, dayTasks)

    if (maxOverlap <= 2) {
      return baseWidth
    }

    return Math.max(maxOverlap * MIN_EVENT_COLUMN_WIDTH, baseWidth)
  })

  const rawTotal = rawWidths.reduce((s, w) => s + w, 0)
  if (rawTotal > availableWidth) {
    return rawWidths.map((w, i) => isEmptyWeekend[i] ? w : Math.max(w, MIN_DAY_WIDTH))
  }
  const scale = rawTotal > 0 ? availableWidth / rawTotal : 1
  return rawWidths.map((w, i) => isEmptyWeekend[i] ? w * scale : Math.max(w * scale, MIN_DAY_WIDTH))
}

export const calculateDragSelection = (anchorMinutes: number, currentMinutes: number): SelectionPreview => {
  const startMin = Math.min(anchorMinutes, currentMinutes)
  const endMin = Math.max(anchorMinutes, currentMinutes)
  return { startMinutes: startMin, endMinutes: Math.max(endMin, startMin + 15) }
}

export const formatTimeFromMinutes = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60) % 24
  const minutes = totalMinutes % 60
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours
  return minutes === 0 ? `${displayHour} ${period}` : `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`
}

export type TaskColumn = {
  event: TaskCalendarEvent
  column: number
  totalColumns: number
}

type CombinedEvent = {
  id: string
  start: string
  end: string
  itemType: 'event' | 'consultation' | 'task'
  event?: DateRangeViewEvent
  taskEvent?: TaskCalendarEvent
  consultation?: ConsultationCalendarEvent
}

export type CombinedColumn = {
  id: string
  column: number
  totalColumns: number
  isConsultation: boolean
  event?: DateRangeViewEvent
  taskEvent?: TaskCalendarEvent
  consultation?: ConsultationCalendarEvent
}

const getVisualEndTimeCombined = (item: CombinedEvent): number => {
  const start = dayjs(item.start).valueOf()
  const end = dayjs(item.end).valueOf()
  return Math.max(end, start + MIN_VISUAL_DURATION_MS)
}

const combinedEventsOverlap = (a: CombinedEvent, b: CombinedEvent): boolean => {
  const aStart = dayjs(a.start).valueOf()
  const aEnd = getVisualEndTimeCombined(a)
  const bStart = dayjs(b.start).valueOf()
  const bEnd = getVisualEndTimeCombined(b)
  return aStart < bEnd && bStart < aEnd
}

export const calculateCombinedColumns = (
  events: DateRangeViewEvent[],
  consultations: ConsultationCalendarEvent[],
  tasks: TaskCalendarEvent[] = []
): { eventColumns: EventColumn[]; consultationColumns: ConsultationColumn[]; taskColumns: TaskColumn[] } => {
  const combinedItems: CombinedEvent[] = [
    ...events.map(e => ({
      id: `event-${e.id}`,
      start: e.start,
      end: e.end,
      itemType: 'event' as const,
      event: e,
    })),
    ...consultations.map(c => ({
      id: `consultation-${c.id}`,
      start: c.start,
      end: c.end,
      itemType: 'consultation' as const,
      consultation: c,
    })),
    ...tasks.map(e => ({
      id: `task-${e.id}`,
      start: e.start,
      end: e.end,
      itemType: 'task' as const,
      taskEvent: e,
    })),
  ]

  if (combinedItems.length === 0) {
    return { eventColumns: [], consultationColumns: [], taskColumns: [] }
  }

  const sortedItems = [...combinedItems].sort((a, b) =>
    dayjs(a.start).valueOf() - dayjs(b.start).valueOf()
  )

  const clusters: CombinedEvent[][] = []

  for (const item of sortedItems) {
    let addedToCluster = false

    for (const cluster of clusters) {
      const overlapsWithCluster = cluster.some(e => combinedEventsOverlap(e, item))
      if (overlapsWithCluster) {
        cluster.push(item)
        addedToCluster = true
        break
      }
    }

    if (!addedToCluster) {
      clusters.push([item])
    }
  }

  const eventColumns: EventColumn[] = []
  const consultationColumns: ConsultationColumn[] = []
  const taskColumns: TaskColumn[] = []

  for (const cluster of clusters) {
    const clusterSorted = [...cluster].sort((a, b) =>
      dayjs(a.start).valueOf() - dayjs(b.start).valueOf()
    )

    const columnEnds: number[] = []
    const clusterColumns: { item: CombinedEvent; column: number }[] = []

    for (const item of clusterSorted) {
      const itemStart = dayjs(item.start).valueOf()
      const itemEnd = getVisualEndTimeCombined(item)

      let assignedColumn = -1
      for (let i = 0; i < columnEnds.length; i++) {
        if (columnEnds[i] <= itemStart) {
          assignedColumn = i
          columnEnds[i] = itemEnd
          break
        }
      }

      if (assignedColumn === -1) {
        assignedColumn = columnEnds.length
        columnEnds.push(itemEnd)
      }

      clusterColumns.push({ item, column: assignedColumn })
    }

    const totalColumns = columnEnds.length

    for (const col of clusterColumns) {
      if (col.item.itemType === 'consultation' && col.item.consultation) {
        consultationColumns.push({
          consultation: col.item.consultation,
          column: col.column,
          totalColumns,
        })
      } else if (col.item.itemType === 'task' && col.item.taskEvent) {
        taskColumns.push({
          event: col.item.taskEvent,
          column: col.column,
          totalColumns,
        })
      } else if (col.item.itemType === 'event' && col.item.event) {
        eventColumns.push({
          event: col.item.event,
          column: col.column,
          totalColumns,
        })
      }
    }
  }

  return { eventColumns, consultationColumns, taskColumns }
}
